export const LINK_TYPES = [
  "web", "instagram", "facebook", "tiktok", "venmo",
] as const;

export type LinkType = (typeof LINK_TYPES)[number];

export interface EventLink {
  type: LinkType;
  url: string;
  label?: string;
}

/** Normalises a raw string to a URL and infers the platform. */
export function detectLinkType(raw: string): LinkType {
  try {
    const url = raw.startsWith("http") ? raw : `https://${raw}`;
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    if (host === "instagram.com") return "instagram";
    if (host === "facebook.com" || host === "fb.com" || host === "fb.me") return "facebook";
    if (host === "tiktok.com") return "tiktok";
    if (host === "venmo.com") return "venmo";
    return "web";
  } catch {
    return "web";
  }
}

/** Returns the meaningful human-readable part of a link URL. */
export function getLinkDisplay(link: EventLink): string {
  try {
    const url = new URL(link.url);
    const path = url.pathname.replace(/\/$/, "");
    switch (link.type) {
      case "instagram":
      case "tiktok": {
        const handle = path.replace(/^\//, "").replace(/^@/, "");
        return handle ? `@${handle}` : link.type;
      }
      case "venmo": {
        const handle = path.replace(/^\//, "");
        return handle ? `@${handle}` : "Venmo";
      }
      case "facebook": {
        const page = path.replace(/^\//, "");
        return page || "Facebook";
      }
      case "web":
        return url.hostname.replace(/^www\./, "");
    }
  } catch {
    return link.url;
  }
}

export function isLinkType(value: unknown): value is LinkType {
  return typeof value === "string" && (LINK_TYPES as readonly string[]).includes(value);
}

export function normalizeLink(raw: string): EventLink {
  const url = raw.startsWith("http") ? raw : `https://${raw}`;
  return { type: detectLinkType(url), url };
}
